/**
 * The rules that tie a companion feed to the rest of its choice (ADR-142's amendment of 2026-10-30, decision 3): a feed
 * takes exactly one worm, `fed` and `befriended` live in a choice's `do` and `accepted` in its check's `onPass`, one feed
 * a list, `accepted` only on a feed and `befriended` only with no check. Each action's own shape is `checkActions`'. And the
 * rules of the offer's accepting choice (ADR-142 decisions 5.2 and 5.4, P2.26-T7): a `companionJoin` is the whole of a
 * choice's `do`, on a choice with no check that is gated on `{ companion: "offered" }` and ends the conversation, since the
 * server then asks for the name and writes the join with it. Pure.
 */

import { isPlain } from "./dialogue-check.ts";

/**
 * The feeds a list names, ignoring anything that is not an action object.
 *
 * @param value The list.
 * @returns The feeds, in order.
 */
function feedsOf(value: unknown): unknown[] {
  return Array.isArray(value) ? value.filter((action) => isPlain(action) && "companionFeed" in action).map((action) => (action as { readonly companionFeed: unknown }).companionFeed) : [];
}

/**
 * How many `takeItem` actions a list holds.
 *
 * @param value The list.
 * @returns The count.
 */
function takesOf(value: unknown): number {
  return Array.isArray(value) ? value.filter((action) => isPlain(action) && "takeItem" in action).length : 0;
}

/**
 * Checks a choice's feeds against the rest of the choice.
 *
 * @param choice The choice object.
 * @param report Records a problem at a path inside the node.
 * @param here The choice's path.
 */
export function companionFeedProblems(choice: { readonly [key: string]: unknown }, report: (field: string, message: string) => void, here: string): void {
  const feeds = feedsOf(choice["do"]);
  const check = isPlain(choice["check"]) ? choice["check"] : null;
  if (feeds.length > 1) {
    report(`${here}.do`, "a choice feeds its companion at most once");
  } else if (feeds[0] === "accepted") {
    report(`${here}.do`, "accepted belongs in a check's onPass, not in do");
  } else if (feeds[0] === "befriended" && check !== null) {
    report(`${here}.do`, "a befriended feed has no check: the rare worm wins him at once");
  } else if (feeds.length === 1 && takesOf(choice["do"]) !== 1) {
    report(`${here}.do`, "a feed takes exactly one item with takeItem");
  }
  if (check === null) {
    return;
  }
  const passes = feedsOf(check["onPass"]);
  if (passes.length > 1 || passes.some((feed) => feed !== "accepted")) {
    report(`${here}.check.onPass`, "a check's onPass feeds only with one accepted");
  } else if (passes.length === 1 && feeds[0] !== "fed") {
    report(`${here}.check.onPass`, "accepted counts a worm only on a choice whose do holds companionFeed fed");
  }
}

/**
 * Whether a list holds a `companionJoin`, ignoring anything that is not an action object.
 *
 * @param value The list.
 * @returns How many it holds.
 */
function joinsOf(value: unknown): number {
  return Array.isArray(value) ? value.filter((action) => isPlain(action) && "companionJoin" in action).length : 0;
}

/**
 * Checks the offer's accepting choice against the rest of the choice, and that no check's `onPass` joins.
 *
 * @param choice The choice object.
 * @param report Records a problem at a path inside the node.
 * @param here The choice's path.
 */
export function companionJoinProblems(choice: { readonly [key: string]: unknown }, report: (field: string, message: string) => void, here: string): void {
  const check = isPlain(choice["check"]) ? choice["check"] : null;
  if (check !== null && joinsOf(check["onPass"]) > 0) {
    report(`${here}.check.onPass`, "a check's onPass never accepts the offer: the accepting choice has no check");
  }
  if (joinsOf(choice["do"]) === 0) {
    return;
  }
  const actions = choice["do"] as readonly unknown[];
  if (check !== null || actions.length !== 1) {
    report(`${here}.do`, "the offer's accepting choice does companionJoin alone and has no check");
    return;
  }
  const when = Array.isArray(choice["when"]) ? choice["when"] : [];
  if (!when.some((condition) => isPlain(condition) && condition["companion"] === "offered")) {
    report(`${here}.when`, "the offer's accepting choice is shown only while { companion: \"offered\" } holds");
  }
  if (choice["next"] !== null) {
    report(`${here}.next`, "the offer's accepting choice ends the conversation: next is null");
  }
}

/**
 * Checks the choice that calls the companion out of his perch (ADR-142 decision 6.2, P2.26-T6): it does `companionCall`
 * alone, has no check, and is shown only while he is home, so it is never offered to a character whose companion is
 * already out. A `companionCall` anywhere else (an `onPass`, beside another action) is refused.
 *
 * @param choice The choice object.
 * @param report Records a problem at a path inside the node.
 * @param here The choice's path.
 */
export function companionCallProblems(choice: { readonly [key: string]: unknown }, report: (field: string, message: string) => void, here: string): void {
  const check = isPlain(choice["check"]) ? choice["check"] : null;
  const called = (value: unknown): number => (Array.isArray(value) ? value.filter((action) => isPlain(action) && "companionCall" in action).length : 0);
  if (check !== null && called(check["onPass"]) + called(check["onFail"]) > 0) {
    report(`${here}.check.onPass`, "a check never calls the companion: the calling choice has no check");
  }
  if (called(choice["do"]) === 0) {
    return;
  }
  const actions = choice["do"] as readonly unknown[];
  if (check !== null || actions.length !== 1) {
    report(`${here}.do`, "the choice that calls the companion does companionCall alone and has no check");
    return;
  }
  const when = Array.isArray(choice["when"]) ? choice["when"] : [];
  if (!when.some((condition) => isPlain(condition) && condition["companion"] === "home")) {
    report(`${here}.when`, "the choice that calls the companion is shown only while { companion: \"home\" } holds");
  }
}
